import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import Deployment from "../../services/deployment";
import Toolbar from "../controls/Toolbar";
import ConfirmationPrompt from "../controls/ConfirmationPrompt";
import TextBox from "../controls/form/TextBox";
import PubSub from "../../services/PubSub";

import { useHasPermission } from "../../common/Hooks";
import { useTranslation } from "react-i18next";
import Utils from "../../utils/utils";
import ACTION_TYPES from "../../config/actionTypes.js"
import ActionButton from "../settings/ActionButton.jsx";
import Label from "../controls/form/Label.jsx";

const DeploymentDetails = () => {
    const { objectSubTypeId } = useParams();
    const [deployment, setDeployment] = useState({});
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: deployment.environment ? deployment.environment._id : null,
        deployment: objectSubTypeId,
        type: "deployment"
    });
    const [translate] = useTranslation();
    const yupValidation = Yup.object({
        name: Yup.string().min(2, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 2 })).required(" "),
        homePage: Yup.string().matches(/^\/[0-9a-zA-Z-_]*$|^$/, translate("OTM_INVALID_HOME_PAGE"))
    });

    const allowedUpdate = useMemo(() => {
        return permissions.update && deployment.actionInProgress === ACTION_TYPES.AVAILABLE;
    }, [permissions.update, deployment]);

    const getDeployment = useCallback(async (objectSubTypeId) => {
        const response = await Deployment.getDeployment(objectSubTypeId);
        if (!response.error) {
            setDeployment(response.data);

            setShowSaveButton(false);
        }
    }, []);

    useEffect(() => {
        if (objectSubTypeId) {
            getDeployment(objectSubTypeId);
        }
    }, [objectSubTypeId, getDeployment]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-deployment", () => {
            getDeployment(objectSubTypeId);
        });

        return () => PubSub.unsubscribe("refresh-deployment", subscriptionId);
    }, [objectSubTypeId, getDeployment]);

    useEffect(() => {
        if (showSaveButton) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [showSaveButton]);

    const updateDeployment = useCallback(async () => {
        const requestBody = {
            name: deployment.name,
            description: deployment.description,
            productVersion: deployment.productVersion,
            homePage: deployment.homePage,
            deploymentInProgress: deployment.deploymentInProgress,
            downloadInProgress: deployment.downloadInProgress
        };

        const response = await Deployment.updateDeployment(objectSubTypeId, requestBody);
        if (!response.error) {
            PubSub.publish("refresh-environment-deployments", { deployment });
            setShowSaveButton(false);
            await getDeployment(objectSubTypeId);
        }
    }, [deployment, objectSubTypeId, setShowSaveButton, getDeployment]);

    const deleteDeployment = useCallback(async () => {
        const response = await Deployment.deleteDeployment(objectSubTypeId);
        if (!response.error) {
            setShowConfirmation(false);
            PubSub.publish("refresh-environment-deployments", { deletedDeployment: deployment });
        }
    }, [objectSubTypeId, deployment, setShowConfirmation]);

    const handleDelete = () => {
        setShowConfirmation(true);
    };

    const renderDeployment = useMemo(() => {
        if (!permissions.read) {
            return (
                <div className="p-4">
                    {translate("OTM_NO_PERMISSION_DEPLOYMENT")}
                </div>
            );
        }

        return (
            <>
                <ConfirmationPrompt show={showConfirmation} message={translate("OTM_DELETE_DEPLOYMENT_?")}
                    onConfirm={deleteDeployment} onCancel={() => setShowConfirmation(false)} />
                <div className="col-12 mt-3">
                    <div className="card h-100">
                        <div className="card-header border-bottom">
                            <Toolbar
                                buttons={[
                                    {
                                        name: "Save",
                                        title: translate("OTM_SAVE"),
                                        onClick: updateDeployment,
                                        hidden: !showSaveButton
                                    },
                                    {
                                        name: "DeleteOutlined",
                                        title: translate("OTM_DELETE"),
                                        onClick: handleDelete,
                                        disabled: !permissions.delete || deployment.deploymentInProgress || deployment.downloadInProgress
                                    }
                                ]}
                                heading={translate("OTM_DEPLOYMENT_DETAILS")}
                            />
                        </div>
                        <div className="card-body">
                            <Formik initialValues={{
                                name: deployment.name || "",
                                description: deployment.description || "",
                                homePage: deployment.homePage || "",
                                deploymentId: deployment._id || ""
                            }} validationSchema={yupValidation}
                                enableReinitialize>
                                <Form onChange={(event) => {
                                    setDeployment({ ...deployment, [event.target.name]: event.target.value });
                                    setShowSaveButton(yupValidation.isValidSync({ ...deployment, [event.target.name]: event.target.value }));
                                }}>
                                    <div className="row mt-n3">
                                        <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_NAME")} type="text" name="name" readOnly={!allowedUpdate} />
                                        <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_DESCRIPTION")} type="text" name="description" readOnly={!allowedUpdate} />
                                        <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_HOME_PAGE")} type="text" name="homePage" readOnly={!allowedUpdate} />
                                        <div className="col-12 col-lg-6 mt-3">
                                            <Label
                                                controlId={"deploymentId"}
                                                label={translate("OTM_DEPLOYMENT_ID")}
                                            />
                                            <div className="row gx-2 align-items-center">
                                                <div className="col">
                                                    <TextBox css="" type="text" name="deploymentId" disabled={true} readOnly={true} />
                                                </div>
                                                <div className="col-auto">
                                                    <ActionButton actionType="copy" valueToCopy={deployment._id} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            </Formik>
                        </div>
                    </div>
                </div>
            </>
        );

    }, [deleteDeployment, deployment, permissions, allowedUpdate, yupValidation, showConfirmation, showSaveButton, translate, updateDeployment])

    return (
        <>
            {renderDeployment}
        </>
    );
};

export default DeploymentDetails;
